import { useEffect, useState } from 'react';

/**
 * Whether the `.dark` class is currently present on `<html>` — the same signal
 * the app's `dark:` Tailwind variant reacts to (set by `genDsDarkModeScript` in
 * config/app-index.ts). Returns `false` on the server.
 *
 * Exported so callers outside the React tree (e.g. the global tracking decorator)
 * can read the same signal without needing `useIsDarkMode`'s mount/observer lifecycle.
 */
export const isDarkModeApplied = (): boolean => {
	if (typeof document === 'undefined') return false; // SSR safety

	return document.documentElement.classList.contains('dark');
};

/**
 * Tracks the `.dark` class on `<html>` — see {@link isDarkModeApplied}.
 * Returns `false` on the server and on the first client render, then updates
 * once mounted and stays in sync with future class changes (e.g. a live OS
 * `prefers-color-scheme` change).
 *
 * @example
 * ```tsx
 * const isDark = useIsDarkMode();
 * ```
 */
export const useIsDarkMode = (): boolean => {
	const [isDark, setIsDark] = useState(false);

	useEffect(() => {
		const update = (): void => setIsDark(isDarkModeApplied());

		update();
		const observer = new MutationObserver(update);
		observer.observe(document.documentElement, { attributeFilter: ['class'] });
		return () => observer.disconnect();
	}, []);

	return isDark;
};
